import React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import { TidewaysContext, ContextRoute } from './app/context'
import { Entity } from './app/entity'
import { Trace, traceSpanCategories } from './domain/Trace'
import Link from './helper/link'
import Loading from './helper/loading'
import { PortalContentNavigation } from './helper/portal-content-navigation'
import { parseQueryString } from './helper/queryString'
import { allowedDurationsForTracepoints, maxConcurrentTracepoints, planSupportsTracepoints } from './helper/tracepoints'
import ShareTraceForm from './monitor/operation/share-trace/share-trace-form'
import Tracepoint from './monitor/operation/tracepoint'
import ProfileList from './profile-list'
import { ApplicationState, isFeatureEnabled } from './redux/ApplicationState'
import { TraceEntity } from './redux/traces/TracesState'
import { fetchTrace } from './redux/V2/Trace/actions'
import { getTrace, isTraceLoaded } from './redux/V2/Trace/TraceState'
import Router from './router'
import Bottlenecks from './trace/bottlenecks'
import { Callgraph } from './trace/callgraph'
import Compare from './trace/compare'
import { FlamegraphComponent } from './trace/flamegraph'
import NoCallgraphAttached from './trace/no-callgraph-attached'
import TraceOverview from './trace/overview'
import RelatedTraces from './trace/related-traces'
import Timeline from './trace/timeline'
import { Container } from './ui/grid'
import { isAggregatedTrace } from './domain/TraceSummary'
import FlamegraphBasedSummary from './trace/flamegraph-based-summary'
import SpanBasedSummary from './trace/span-based-summary'
import AddTracepointForm from './monitor/operation/tracepoint-form/add-tracepoint-form'

interface MatchParameters {
    profile: string
}

interface OwnProps {
    view?: 'timeline' | 'callgraph' | 'summary' | 'bottlenecks' | 'compare' | 'related-traces' | 'linked-traces' | 'flamegraph'
}

interface DispatchProps {
    fetchTrace: (id: string) => void
}

type StateProps = {
    tidewaysContext: TidewaysContext
    router: Router
    route: ContextRoute
    trace: Trace | null
    traceId: string
    traceLoaded?: boolean
    includesDynamicallyInstrumentedFunctions: boolean
    sidebarMenuEnabled: boolean
} & Entity<TraceEntity | null>

type Props = OwnProps & DispatchProps & StateProps & RouteComponentProps<MatchParameters>

interface State {
    addTracepoint: boolean
    tracepointError: string | null
    shareTrace: boolean
}

export class TraceComponent extends React.Component<Props, State> {
    public state: State = {
        addTracepoint: false,
        tracepointError: null,
        shareTrace: false,
    }

    public componentDidMount() {
        this.setRoutingDefaultParameters()
        this.fetchTrace()
    }

    public componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.traceId !== prevProps.traceId) {
            this.setRoutingDefaultParameters()
            this.fetchTrace()
        }
    }

    public fetchTrace = () => {
        if (!this.props.traceLoaded) {
            this.props.fetchTrace(this.props.traceId)
        }
    }

    public findTracepointEvent() {
        // TODO: why do we have this.props.data.trace and this.props.trace?
        if (
            !this.props.data ||
            !this.props.data.traces.length ||
            !this.props.data.traces[0].globalEvents ||
            !this.props.trace ||
            !this.props.trace.xhprofReport ||
            !this.props.trace.xhprofReport.overallTotals
        ) {
            return null
        }

        const tracePointEvents = this.props.data.traces[0].globalEvents.filter(event => event.name === 'tracepoint-start')

        if (tracePointEvents.length === 1) {
            const startTime = (tracePointEvents[0].ts / 1000).toFixed(0)

            if (parseInt(startTime, 10) === 0) {
                // "tracepoint-start" event happens so early, don't even show the event
                return null
            }

            return {
                duration: (this.props.trace.xhprofReport.overallTotals.wt / 1000).toFixed(0),
                startTime,
            }
        }

        return null
    }

    public render() {
        const { router } = this.props
        let { view: active } = this.props

        if (this.props.error?.msg === 'Banned trial host' && this.props.error.status === 403) {
            router.replace('XhprofBillingBundle.Checkout.start', {})
            return null
        }

        if (!this.props.data || !this.props.data.profile || !this.props.data.traces) {
            return <Loading />
        }

        // aggregated traces do not have proper spans, therefore default to flamegraph
        if (active === 'timeline' && isAggregatedTrace(this.props.data.profile)) {
            active = 'flamegraph'
        }

        const sharingStatus = this.props.data.profile.hasActiveShare ? 'Is Shared' : 'Share'
        const rootTrace = this.props.data.traces[0]
        const startTracepointEvent = this.findTracepointEvent()

        const linkedTraceIds = rootTrace?.spans[0]?.annotations?.trace_aggregation_report?.trace_ids

        return (
            <div>
                <PortalContentNavigation>
                    <div style={{ background: 'white' }}>
                        <Container>
                            <TraceOverview
                                profile={this.props.data.profile}
                                bottlenecks={this.props.data.bottlenecks}
                                bottleneckTypes={this.props.data.bottleneckTypes}
                                compact
                                operation={this.props.data.operation}
                            />
                        </Container>
                    </div>
                </PortalContentNavigation>

                {this.state.addTracepoint ? (
                    <AddTracepointForm
                        operation={this.props.data.operation}
                        allowedDurations={allowedDurationsForTracepoints(this.props.tidewaysContext)}
                        error={this.state.tracepointError}
                        annotationDefaults={rootTrace.spans[0].annotations}
                        onExit={() => this.setState({ addTracepoint: false })}
                    />
                ) : null}

                {this.state.shareTrace && this.props.trace ? (
                    <ShareTraceForm
                        trace={this.props.trace}
                        onExit={() => this.setState({ shareTrace: false })}
                        traceSummary={this.props.data.profile}
                        hasActiveShare={this.props.data.profile.hasActiveShare}
                        allowedToShare={this.props.tidewaysContext.allowedToShare}
                        defaultRetentionDays={this.props.tidewaysContext.application.retentionDays}
                    />
                ) : null}

                {!this.state.addTracepoint && !this.state.shareTrace ? (
                    <React.Fragment>
                        <div className="row">
                            <div className="col-lg-8 col-md-12">
                                <ul className="nav nav-pills nav-pills-tideways">
                                    {isAggregatedTrace(this.props.data.profile) ? null : (
                                        <li className={active === 'timeline' ? 'active' : ''}>
                                            <Link route="XhprofProfilerBundle.Profile.quick">Timeline</Link>
                                        </li>
                                    )}
                                    <li className={active === 'flamegraph' ? 'active' : ''}>
                                        <Link route="XhprofProfilerBundle.Profile.flamegraph">Flamegraph</Link>
                                    </li>
                                    <li className={active === 'summary' ? 'active' : ''}>
                                        <Link route="XhprofProfilerBundle.Profile.summary">Summary</Link>
                                    </li>
                                    <li className={active === 'callgraph' ? 'active' : ''}>
                                        <Link route="XhprofProfilerBundle.Profile.callgraph">Callgraph</Link>
                                    </li>
                                    {this.props.data.hasBottlenecks ? (
                                        <li className={active === 'bottlenecks' ? 'active' : ''}>
                                            <Link route="XhprofProfilerBundle.Profile.bottlenecksReport">
                                                Bottlenecks{' '}
                                                <span className="label label-danger">{this.props.data.bottlenecksReport.length}</span>
                                            </Link>
                                        </li>
                                    ) : (
                                        ''
                                    )}
                                    {linkedTraceIds ? (
                                        <li className={active === 'linked-traces' ? 'active' : ''}>
                                            <Link
                                                route="XhprofProfilerBundle.Profile.linkedTraces"
                                                query={{ onlyTraceIds: linkedTraceIds }}
                                            >
                                                Traces Used For Aggregation
                                            </Link>
                                        </li>
                                    ) : null}
                                    {this.props.data.profile.correlationId && !linkedTraceIds ? (
                                        <li className={active === 'linked-traces' ? 'active' : ''}>
                                            <Link
                                                route="XhprofProfilerBundle.Profile.linkedTraces"
                                                query={{ cid: this.props.data.profile.correlationId }}
                                            >
                                                Linked Traces
                                            </Link>
                                        </li>
                                    ) : null}
                                    {this.hasTraceHierachyWithChildren() ? (
                                        <li className={active === 'related-traces' ? 'active' : ''}>
                                            <Link route="XhprofProfilerBundle.Profile.relatedTraces">Related</Link>
                                        </li>
                                    ) : null}
                                    <li className={'pull-right' + (active === 'compare' ? ' active' : '')}>
                                        <Link
                                            route="XhprofProfilerBundle.Profile.listCompare"
                                            query={{ operation: this.props.data.operation.id }}
                                        >
                                            Compare
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                            <div className="col-md-4">
                                <div className="pull-right" style={{ marginLeft: '1em' }}>
                                    <a
                                        className="btn btn-default"
                                        onClick={() => {
                                            this.setState({ shareTrace: true })
                                        }}
                                    >
                                        <i className="fa fa-share" /> {sharingStatus}
                                    </a>
                                </div>
                                <div className="pull-right" style={{ fontSize: '1.3em' }}>
                                    {planSupportsTracepoints(this.props.tidewaysContext) && (
                                        <Tracepoint
                                            small={false}
                                            maxConcurrent={maxConcurrentTracepoints(this.props.tidewaysContext)}
                                            onAdd={errorMessage => this.setState({ addTracepoint: true, tracepointError: errorMessage })}
                                            operation={this.props.data.operation}
                                            service={this.props.tidewaysContext.service}
                                            environment={this.props.tidewaysContext.environment}
                                        />
                                    )}
                                </div>
                                <div className="pull-right">
                                    <Link className="btn btn-default" route="XhprofOrganizationBundle.Application.trigger">
                                        Trigger Trace
                                    </Link>
                                </div>
                            </div>
                        </div>
                        {(() => {
                            const queryParameters = parseQueryString(this.props.location.search)
                            switch (active) {
                                case 'timeline':
                                    return (
                                        <Timeline
                                            categories={traceSpanCategories}
                                            traces={this.props.data.traces}
                                            router={router}
                                            hasCallgraph={this.props.data.hasCallgraph}
                                            hasMemory={this.props.data.hasMemory}
                                            params={queryParameters}
                                            operationId={this.props.data.operation.id}
                                            bottleneckIdentifications={this.props.data.bottleneckIdentifications}
                                            layerReport={this.props.data?.profile?.layers}
                                        />
                                    )
                                case 'summary':
                                    return isAggregatedTrace(this.props.data.profile) ? (
                                        <FlamegraphBasedSummary
                                            flamegraph={rootTrace.flamegraph}
                                            categories={traceSpanCategories}
                                            router={router}
                                            params={queryParameters}
                                            layerReport={this.props.data.profile.layers}
                                        />
                                    ) : (
                                        <SpanBasedSummary
                                            spans={rootTrace.spans}
                                            categories={traceSpanCategories}
                                            router={router}
                                            params={queryParameters}
                                            layerReport={this.props.data.profile.layers}
                                        />
                                    )
                                case 'bottlenecks':
                                    return (
                                        <Bottlenecks
                                            messages={this.props.data?.bottlenecksReport}
                                            spans={rootTrace.spans}
                                            params={queryParameters}
                                            router={router}
                                        />
                                    )
                                case 'callgraph':
                                    return this.props.data.hasCallgraph && this.props.trace ? (
                                        <Callgraph
                                            trace={this.props.trace}
                                            router={router}
                                            url={router.path('XhprofProfilerBundle.Profile.callgraphData', this.props.match.params)}
                                            params={queryParameters}
                                            startTracepointEvent={startTracepointEvent}
                                            showPlusWhenPositive={false}
                                            includesDynamicallyInstrumentedFunctions={this.props.includesDynamicallyInstrumentedFunctions}
                                            bottleneckIdentifications={this.props.data.bottleneckIdentifications}
                                            sidebarMenuEnabled={this.props.sidebarMenuEnabled}
                                            bottlenecks={this.props.data?.bottlenecksReport || []}
                                        />
                                    ) : (
                                        <NoCallgraphAttached profile={this.props.data.profile} />
                                    )
                                case 'compare':
                                    return <Compare operation={this.props.data.operation} params={this.props.match.params} />
                                case 'related-traces':
                                    return (
                                        <RelatedTraces
                                            traceHierarchy={this.props.data?.traceHierarchy}
                                            currentTraceId={this.props.traceId}
                                        />
                                    )
                                case 'linked-traces':
                                    return (
                                        <ProfileList
                                            showTabs={false}
                                            fixedCriteria={{ cid: true }}
                                            listRoute={'XhprofProfilerBundle.Profile.linkedTraces'}
                                            params={{ profile: this.props.traceId }}
                                        />
                                    )
                                case 'flamegraph':
                                    return (
                                        <FlamegraphComponent
                                            spans={rootTrace.spans}
                                            data={rootTrace.flamegraph}
                                            categories={traceSpanCategories}
                                            router={router}
                                            params={queryParameters}
                                            isAggregatedTrace={isAggregatedTrace(this.props.data.profile)}
                                            layerReport={this.props.data.profile.layers}
                                        />
                                    )
                            }
                        })()}
                    </React.Fragment>
                ) : null}
            </div>
        )
    }

    private hasTraceHierachyWithChildren(): boolean {
        if (!this.props.data) {
            return false
        }

        if (!this.props.tidewaysContext.application.license.distributedTracing) {
            return false
        }

        return this.props.data?.traceHierarchy?.nodes.length > 0 && this.props.data?.traceHierarchy?.nodes[0].children.length > 0
    }

    private setRoutingDefaultParameters() {
        this.props.router.setDefaultParameter('base', this.props.match.params.profile)
        this.props.router.setDefaultParameter('profile', this.props.match.params.profile)
    }
}

export default withRouter(
    connect<StateProps, DispatchProps, RouteComponentProps<MatchParameters> & OwnProps, ApplicationState>(
        (globalState, props): StateProps => {
            const traceId = props.match.params.profile
            const traceOld = globalState.traces.traces[traceId] || new Entity()

            return {
                tidewaysContext: globalState.tidewaysContext,
                router: globalState.tidewaysContext.router,
                route: globalState.tidewaysContext.route,
                trace: getTrace(globalState, traceId),
                traceId,
                traceLoaded: isTraceLoaded(globalState, traceId),
                includesDynamicallyInstrumentedFunctions: globalState.tidewaysContext.application.includesDynamicallyInstrumentedFunctions,
                sidebarMenuEnabled: isFeatureEnabled(globalState, 'sidebar_menu'),
                ...traceOld,
            }
        },
        { fetchTrace },
    )(TraceComponent),
)
